import { AfterViewInit, Directive, ElementRef, Input, OnDestroy } from '@angular/core';

/**
 * Adds a sticky horizontal scrollbar to the host scroll container.
 * Use [stickyScrollbarPosition]="'bottom'" to attach at the bottom instead of the top.
 */
@Directive({ selector: '[appStickyScrollbar]' })
export class StickyScrollbarDirective implements AfterViewInit, OnDestroy {

  @Input() stickyScrollbarPosition: 'top' | 'bottom' = 'top';

  private mirror: HTMLElement;
  private mirrorInner: HTMLElement;
  private wrapper: HTMLElement;
  private resizeObserver: ResizeObserver;
  private mutationObserver: MutationObserver;
  private onMirrorScroll: () => void;
  private onWrapperScroll: () => void;

  constructor(private el: ElementRef) {}

  private get isBottom() { return this.stickyScrollbarPosition === 'bottom'; }

  ngAfterViewInit() {
    console.log("!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! Initializing Sticky Scrollbar Directive");
    const host = this.el.nativeElement as HTMLElement;

    // Create the mirror as a direct child of the host so sticky is relative
    // to this element's own scroll context (not some deep ancestor)
    const bottom = this.isBottom;
    this.mirror = document.createElement('div');
    this.mirrorInner = document.createElement('div');
    this.mirror.style.cssText = `
      position: sticky;
      ${bottom ? 'bottom' : 'top'}: 0;
      left: 0;
      overflow-x: auto;
      overflow-y: hidden;
      height: 14px;
      z-index: 10;
      background: #f8f9fa;
      ${bottom ? 'border-top' : 'border-bottom'}: 1px solid #dee2e6;
    `;
    this.mirrorInner.style.cssText = 'height: 1px; display: block;';
    this.mirror.appendChild(this.mirrorInner);

    if (bottom) {
      host.appendChild(this.mirror);
    } else {
      host.insertBefore(this.mirror, host.firstChild);
    }

    // Watch for p-datatable-wrapper to be added (table renders after data loads)
    this.mutationObserver = new MutationObserver(() => {
      const found = host.querySelector('.p-datatable-wrapper') as HTMLElement;
      if (found && found !== this.wrapper) {
        this.connectWrapper(found);
      } else {
        // The table's content changed (e.g. columns shown / hidden) - re-measure once it has laid out
        this.scheduleSync();
      }
    });
    this.mutationObserver.observe(host, { childList: true, subtree: true });

    // Try immediately in case it already exists
    const existing = host.querySelector('.p-datatable-wrapper') as HTMLElement;
    if (existing) this.connectWrapper(existing);
  }

  private connectWrapper(wrapper: HTMLElement) {
    // Detach from any previous wrapper
    if (this.wrapper && this.onMirrorScroll && this.onWrapperScroll) {
      this.mirror.removeEventListener('scroll', this.onMirrorScroll);
      this.wrapper.removeEventListener('scroll', this.onWrapperScroll);
      this.wrapper.classList.remove('sticky-scroll-managed');
    }
    if (this.resizeObserver) this.resizeObserver.disconnect();

    this.wrapper = wrapper;

    // Hide the native scrollbar on the PrimeNG wrapper — our mirror replaces it
    wrapper.classList.add('sticky-scroll-managed');
    this.ensureHideScrollbarStyle();

    this.onMirrorScroll  = () => wrapper.scrollLeft = this.mirror.scrollLeft;
    this.onWrapperScroll = () => this.mirror.scrollLeft = wrapper.scrollLeft;

    this.mirror.addEventListener('scroll', this.onMirrorScroll);
    wrapper.addEventListener('scroll', this.onWrapperScroll);

    // The wrapper's own box doesn't change when columns are added past its edge - only the table inside it
    // gets wider - so watch the table(s) as well, otherwise the scrollbar width goes stale
    this.resizeObserver = new ResizeObserver(() => this.syncWidth());
    this.resizeObserver.observe(wrapper);
    wrapper.querySelectorAll('table').forEach(table => this.resizeObserver.observe(table));
    this.syncWidth();
  }

  private syncFrame: number | null = null;

  private scheduleSync() {
    if (this.syncFrame !== null) return;
    this.syncFrame = requestAnimationFrame(() => {
      this.syncFrame = null;
      // A table that appeared after the wrapper (or was re-rendered) needs watching too
      if (this.wrapper && this.resizeObserver) {
        this.wrapper.querySelectorAll('table').forEach(table => this.resizeObserver.observe(table));
      }
      this.syncWidth();
    });
  }

  private ensureHideScrollbarStyle() {
    if (document.getElementById('sticky-scroll-style')) return;
    const style = document.createElement('style');
    style.id = 'sticky-scroll-style';
    style.textContent =
      '.sticky-scroll-managed{scrollbar-width:none;-ms-overflow-style:none;}' +
      '.sticky-scroll-managed::-webkit-scrollbar{display:none;}';
    document.head.appendChild(style);
  }

  private syncWidth() {
    if (!this.wrapper || !this.mirror) return;
    this.mirrorInner.style.width = this.wrapper.scrollWidth + 'px';
    this.mirror.style.width      = this.wrapper.clientWidth + 'px';
    this.mirror.style.display    = this.wrapper.scrollWidth > this.wrapper.clientWidth ? '' : 'none';
  }

  ngOnDestroy() {
    if (this.syncFrame !== null) cancelAnimationFrame(this.syncFrame);
    if (this.mutationObserver) this.mutationObserver.disconnect();
    if (this.resizeObserver)   this.resizeObserver.disconnect();
    if (this.wrapper) {
      if (this.onMirrorScroll)  this.mirror.removeEventListener('scroll', this.onMirrorScroll);
      if (this.onWrapperScroll) this.wrapper.removeEventListener('scroll', this.onWrapperScroll);
      this.wrapper.classList.remove('sticky-scroll-managed');
    }
    if (this.mirror) this.mirror.remove();
  }
}
